import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { Carousel } from 'antd';

import { ILoginState, IMenuState, IBenefitsState } from '@share/store/slices';
import { RootState, GetHomeParams, getLocalURI, isExternalUrl, getBenefitInfo } from '@share/utils';
import { BlueButton } from '@share/components';
import { Routes } from '@share/constants';
import { IAccount, INavigationItem, BenefitsEnum } from '@share/common-types';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import {
  faTags,
  faArrowsRotate,
  faSuitcaseRolling,
  faStar,
  faCirclePlay,
  faGraduationCap,
  faAward,
  faPhone,
  faCalendarAlt,
} from '@fortawesome/free-solid-svg-icons';

import HeroVegas from '@assets/images/hilton-hero/vegas.jpg';
import HeroCruise from '@assets/images/hilton-hero/cruise.jpg';
import HeroSantorini from '@assets/images/hilton-hero/santorini.jpg';
import HeroDubai from '@assets/images/hilton-hero/dubai.jpg';
import HeroNewYork from '@assets/images/hilton-hero/new-york.jpg';
import HeroLondon from '@assets/images/hilton-hero/london.jpg';
import HeroZanzibar from '@assets/images/hilton-hero/zanzibar.jpg';
import HeroBeach from '@assets/images/hilton-hero/beach.jpg';
import HeroParis from '@assets/images/hilton-hero/paris.jpg';

import DealEscapesImage from '@assets/images/explore-getaways.jpg';
import DealCruiseImage from '@assets/images/getaways/getaways.jpg';
import ConciergeTeamImage from '@assets/images/hilton-concierge-team.jpg';

// Browse by Experience imagery (existing assets reused; swap for final brand assets when available).
import ExpCondos from '@assets/images/hilton-exp-condos.jpg';
import ExpCruises from '@assets/images/hilton-exp-cruises.jpg';
import ExpGetaways from '@assets/images/hilton-exp-getaways.jpg';
import ExpHotels from '@assets/images/hilton-exp-hotels.jpg';
import ExpAllInclusives from '@assets/images/hilton-exp-all-inclusives.jpg';
import ExpHomes from '@assets/images/hilton-exp-homes.jpg';
import ExpFlights from '@assets/images/flights/bg-flights.jpg';
import ExpAttractions from '@assets/images/hilton-exp-attractions.jpg';

// Trusted by World-Class Partners — Hilton-family brand logos.
import LogoHampton from '@assets/images/hilton-partners/hampton.png';
import LogoTru from '@assets/images/hilton-partners/tru.png';
import LogoHomewood from '@assets/images/hilton-partners/homewood-suites.png';
import LogoGardenInn from '@assets/images/hilton-partners/hilton-garden-inn.png';
import LogoEmbassy from '@assets/images/hilton-partners/embassy-suites.png';
import LogoTapestry from '@assets/images/hilton-partners/tapestry.png';
import LogoCurio from '@assets/images/hilton-partners/curio.png';
import LogoMotto from '@assets/images/hilton-partners/motto.png';
import LogoSpark from '@assets/images/hilton-partners/spark.png';
import LogoGraduate from '@assets/images/hilton-partners/graduate.png';
import LogoHilton from '@assets/images/hilton-partners/hilton.png';
import LogoLxr from '@assets/images/hilton-partners/lxr.png';
import LogoSignia from '@assets/images/hilton-partners/signia.png';
import LogoHgvClub from '@assets/images/hilton-partners/hgv-club.png';
import LogoWaldorf from '@assets/images/hilton-partners/waldorf-astoria.png';
import LogoConrad from '@assets/images/hilton-partners/conrad.png';
import LogoDoubletree from '@assets/images/hilton-partners/doubletree.png';

import './style.scss';

interface IMapStateToProps {
  loginStore: ILoginState;
  menuStore: IMenuState;
  benefitsStore: IBenefitsState;
}

interface IProps extends IMapStateToProps {}

function HomeHiltonComponent(props: IProps) {
  const { loginStore, menuStore, benefitsStore } = props;
  const { account } = loginStore;
  const { items, menuList } = menuStore;
  const { benefits } = benefitsStore;

  const accountName = account?.name;
  const homeParams = GetHomeParams(account as IAccount);
  const to = (route: string) => `/${accountName}${route}${homeParams}`;

  // If the site menu exposes an "Attractions" entry, the Getaways card becomes
  // Attractions and links to that menu item instead.
  const menuEntries: INavigationItem[] = [
    ...(items?.header ?? []),
    ...(items?.main ?? []),
    ...(items?.menu ?? []),
    ...(menuList ?? []),
  ].flatMap((m) => [m, ...(m?.childItems ?? [])]);

  const attractionsItem = menuEntries.find((m) => {
    const title = (m?.title ?? '').toLowerCase();
    const url = (m?.url ?? '').toLowerCase();
    return title.includes('attraction') || url.includes('attraction');
  });

  const heroImages = [
    HeroVegas,
    HeroCruise,
    HeroSantorini,
    HeroDubai,
    HeroNewYork,
    HeroLondon,
    HeroZanzibar,
    HeroBeach,
    HeroParis,
  ];

  // One slide every 20s, auto-advancing, no arrows/dots.
  const heroCarouselSettings = {
    dots: false,
    arrows: false,
    infinite: true,
    autoplay: true,
    autoplaySpeed: 20000,
    speed: 900,
    slidesToShow: 1,
    slidesToScroll: 1,
    pauseOnHover: false,
  };

  const quickLinks = [
    { icon: faTags, title: 'hilton.quick.dailyDeals', titleDefault: 'Daily Deals', desc: 'hilton.quick.dailyDeals.desc', descDefault: 'Save every day on top destinations', route: Routes.DailyBenefits },
    { icon: faArrowsRotate, title: 'hilton.quick.renew', titleDefault: 'I Need to Renew My Membership', desc: 'hilton.quick.renew.desc', descDefault: 'Renew securely in just a few clicks', route: Routes.NewMembers },
    { icon: faSuitcaseRolling, title: 'hilton.quick.beforeYouGo', titleDefault: 'Before You Go', desc: 'hilton.quick.beforeYouGo.desc', descDefault: 'Travel resources, tips & documents', route: Routes.DailyBenefits },
    { icon: faStar, title: 'hilton.quick.americanLifestyle', titleDefault: 'American Lifestyle', desc: 'hilton.quick.americanLifestyle.desc', descDefault: 'Monthly magazine and inspiration', route: Routes.DailyBenefits },
    { icon: faCirclePlay, title: 'hilton.quick.memberVideos', titleDefault: 'Member Videos', desc: 'hilton.quick.memberVideos.desc', descDefault: 'Helpful how-to videos & tutorials', route: Routes.MemberVideos },
    { icon: faGraduationCap, title: 'hilton.quick.masterClasses', titleDefault: 'Master Classes', desc: 'hilton.quick.masterClasses.desc', descDefault: 'Monthly webinars & travel insights', route: Routes.Webinars },
  ];

  const attractionsUrl = attractionsItem?.url ?? '';
  const attractionsExternal = isExternalUrl(attractionsUrl);

  const getawaysOrAttractions = attractionsItem
    ? {
        image: ExpAttractions,
        title: 'hilton.exp.attractions',
        titleDefault: 'Attractions',
        desc: 'hilton.exp.attractions.desc',
        descDefault: 'Tickets to top attractions',
        href: attractionsExternal ? attractionsUrl : to(getLocalURI(attractionsUrl)),
        external: attractionsExternal,
      }
    : {
        image: ExpGetaways,
        title: 'hilton.exp.getaways',
        titleDefault: 'Getaways',
        desc: 'hilton.exp.getaways.desc',
        descDefault: 'Short trips. Big memories.',
        href: to(Routes.Getaways),
        external: false,
      };

  const experiences = [
    { image: ExpCondos, title: 'hilton.exp.condos', titleDefault: 'Condos', desc: 'hilton.exp.condos.desc', descDefault: 'Spacious stays worldwide', href: to(Routes.Search), external: false },
    { image: ExpCruises, title: 'hilton.exp.cruises', titleDefault: 'Cruises', desc: 'hilton.exp.cruises.desc', descDefault: 'Sail to unforgettable destinations', href: to(Routes.Cruises), external: false },
    getawaysOrAttractions,
    { image: ExpHotels, title: 'hilton.exp.hotels', titleDefault: 'Hotels', desc: 'hilton.exp.hotels.desc', descDefault: 'Over 1M+ hotels worldwide', href: to(Routes.Search), external: false },
    { image: ExpAllInclusives, title: 'hilton.exp.allInclusives', titleDefault: 'All Inclusives', desc: 'hilton.exp.allInclusives.desc', descDefault: 'Worry-free vacations', href: to(Routes.AllInclusive), external: false },
    { image: ExpHomes, title: 'hilton.exp.homes', titleDefault: 'Homes', desc: 'hilton.exp.homes.desc', descDefault: 'Private homes anywhere', href: to(Routes.Homes), external: false },
    { image: ExpFlights, title: 'hilton.exp.flights', titleDefault: 'Flights', desc: 'hilton.exp.flights.desc', descDefault: 'Great fares & more', href: to(Routes.Flights), external: false },
  ];

  // Find Inspiration: Fantasy & Cruise getaways pulled from the same benefits data
  // the /getaway page uses (title/description/image/link), rendered as two cards.
  const getawayChildItems: {
    title: string;
    description: string;
    imageUrl: string;
    url: string;
  }[] = getBenefitInfo(benefits, BenefitsEnum.Getaways)?.[0]?.childItems ?? [];

  const findGetaway = (keyword: string) =>
    getawayChildItems.find((c) => (c?.title ?? '').toLowerCase().includes(keyword));

  const inspirationCards = [findGetaway('fantasy'), findGetaway('cruise')]
    .filter((c): c is NonNullable<typeof c> => !!c)
    .map((c) => {
      const external = isExternalUrl(c.url);
      return {
        title: c.title,
        description: c.description,
        imageUrl: c.imageUrl,
        external,
        href: external ? c.url : `/${accountName}${getLocalURI(c.url)}`,
      };
    });

  // Trusted by World-Class Partners — Hilton-family brand logos.
  const partners: { name: string; image?: string }[] = [
    { name: 'Hampton by Hilton', image: LogoHampton },
    { name: 'Tru by Hilton', image: LogoTru },
    { name: 'Homewood Suites by Hilton', image: LogoHomewood },
    { name: 'Hilton Garden Inn', image: LogoGardenInn },
    { name: 'Embassy Suites by Hilton', image: LogoEmbassy },
    { name: 'Tapestry Collection by Hilton', image: LogoTapestry },
    { name: 'Curio Collection by Hilton', image: LogoCurio },
    { name: 'Motto by Hilton', image: LogoMotto },
    { name: 'Spark by Hilton', image: LogoSpark },
    { name: 'Graduate by Hilton', image: LogoGraduate },
    { name: 'Hilton', image: LogoHilton },
    { name: 'LXR Hotels & Resorts', image: LogoLxr },
    { name: 'Signia by Hilton', image: LogoSignia },
    { name: 'Hilton Grand Vacations Club', image: LogoHgvClub },
    { name: 'Waldorf Astoria Hotels & Resorts', image: LogoWaldorf },
    { name: 'Conrad Hotels & Resorts', image: LogoConrad },
    { name: 'DoubleTree by Hilton', image: LogoDoubletree },
  ];

  const partnersCarouselSettings = {
    dots: false,
    arrows: false,
    infinite: true,
    autoplay: true,
    autoplaySpeed: 2500,
    speed: 600,
    slidesToShow: 5,
    slidesToScroll: 1,
    responsive: [
      { breakpoint: 900, settings: { slidesToShow: 3 } },
      { breakpoint: 560, settings: { slidesToShow: 2 } },
    ],
  };

  return (
    <div className="home-hilton">
      {/* Hero */}
      <section className="home-hilton__hero">
        <div className="home-hilton__hero-bg">
          <Carousel {...heroCarouselSettings} className="home-hilton__hero-carousel">
            {heroImages.map((img, i) => (
              <div key={i}>
                <div
                  className="home-hilton__hero-slide"
                  style={{ backgroundImage: `url(${img})` }}
                />
              </div>
            ))}
          </Carousel>
        </div>
        <div className="home-hilton__hero-overlay" />
        <div className="home-hilton__container home-hilton__hero-content">
          <h1 className="home-hilton__hero-title">
            <FormattedMessage id="hilton.hero.title" defaultMessage="Travel more." />
            <span className="home-hilton__hero-title-accent">
              <FormattedMessage id="hilton.hero.subtitle" defaultMessage="Live the Hilton Grand Vacations life." />
            </span>
          </h1>
          <p className="home-hilton__hero-copy">
            <FormattedMessage
              id="hilton.hero.copy"
              defaultMessage="Exclusive member benefits. Extraordinary experiences. Your next escape starts here."
            />
          </p>
          <div className="home-hilton__hero-actions">
            <Link to={to(Routes.Search)} className="home-hilton__hero-primary">
              <FormattedMessage id="hilton.hero.cta.deals" defaultMessage="Explore Deals" />
            </Link>
            <Link to={to(Routes.Reservations)} className="home-hilton__hero-secondary">
              <FormattedMessage id="hilton.hero.cta.bookings" defaultMessage="View My Bookings" />
              <FontAwesomeIcon icon={faCalendarAlt} />
            </Link>
          </div>
        </div>
      </section>

      {/* Quick links */}
      <section className="home-hilton__container home-hilton__quick">
        <div className="home-hilton__quick-row">
          {quickLinks.map((q) => (
            <Link to={to(q.route)} key={q.title} className="home-hilton__quick-item">
              <span className="home-hilton__quick-icon">
                <FontAwesomeIcon icon={q.icon} />
              </span>
              <span className="home-hilton__quick-title">
                <FormattedMessage id={q.title} defaultMessage={q.titleDefault} />
              </span>
              <span className="home-hilton__quick-desc">
                <FormattedMessage id={q.desc} defaultMessage={q.descDefault} />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured Member Deals */}
      <section className="home-hilton__container home-hilton__deals">
        <div className="home-hilton__section-head">
          <h2 className="home-hilton__section-title">
            <FormattedMessage id="hilton.deals.title" defaultMessage="Featured Member Deals" />
          </h2>
          <Link to={to(Routes.Search)} className="home-hilton__section-link">
            <FormattedMessage id="hilton.deals.viewAll" defaultMessage="View all deals" />
            <FontAwesomeIcon icon={faArrowRight} />
          </Link>
        </div>
        <div className="home-hilton__deals-grid">
          <Link
            to={to(Routes.Getaways)}
            className="home-hilton__deal-card"
            style={{ backgroundImage: `url(${DealEscapesImage})` }}
          >
            <div className="home-hilton__deal-body">
              <span className="home-hilton__deal-body-title">
                <h3><FormattedMessage id="hilton.deals.escapes.title" defaultMessage="Last Minute Escapes" /></h3>
                <p><FormattedMessage id="hilton.deals.escapes.copy" defaultMessage="Save up to 60% on luxury getaways" /></p>
              </span>
              <span className="home-hilton__deal-btn">
                <FormattedMessage id="hilton.deals.cta" defaultMessage="See the deals" />
              </span>
            </div>
          </Link>
          <Link
            to={to(Routes.Cruises)}
            className="home-hilton__deal-card"
            style={{ backgroundImage: `url(${DealCruiseImage})` }}
          >
            <div className="home-hilton__deal-body">
              <span className="home-hilton__deal-body-title">
                <h3><FormattedMessage id="hilton.deals.cruise.title" defaultMessage="Amazing Cruise Offers" /></h3>
                <p><FormattedMessage id="hilton.deals.cruise.copy" defaultMessage="Up to $500 Onboard Credit and exclusive perks" /></p>
              </span>
              <span className="home-hilton__deal-btn">
                <FormattedMessage id="hilton.deals.cta" defaultMessage="See the deals" />
              </span>
            </div>
          </Link>
        </div>
      </section>

      {/* Browse by Experience */}
      <section className="home-hilton__container home-hilton__experience">
        <h2 className="home-hilton__section-title">
          <FormattedMessage id="hilton.experience.title" defaultMessage="Browse by Experience" />
        </h2>
        <div className="home-hilton__experience-grid">
          {experiences.map((e) => {
            const inner = (
              <>
                <div
                  className="home-hilton__experience-img"
                  style={{ backgroundImage: `url(${e.image})` }}
                />
                <span className="home-hilton__experience-name">
                  <FormattedMessage id={e.title} defaultMessage={e.titleDefault} />
                </span>
                <span className="home-hilton__experience-desc">
                  <FormattedMessage id={e.desc} defaultMessage={e.descDefault} />
                </span>
              </>
            );

            return e.external ? (
              <a
                href={e.href}
                key={e.title}
                className="home-hilton__experience-card"
                target="_blank"
                rel="noreferrer"
              >
                {inner}
              </a>
            ) : (
              <Link to={e.href} key={e.title} className="home-hilton__experience-card">
                {inner}
              </Link>
            );
          })}
        </div>
      </section>

      {/* Your Personal Concierge Team */}
      <section className="home-hilton__concierge">
        <div className="home-hilton__container home-hilton__concierge-wrapper">
          <div className="home-hilton__concierge-text">
            <h2 className="home-hilton__section-title-concierge">
              <FormattedMessage id="hilton.concierge.title" defaultMessage="Your Personal Concierge Team" />
            </h2>
            <p className="home-hilton__concierge-copy">
              <FormattedMessage
                id="hilton.concierge.copy"
                defaultMessage="We're here to help you plan the perfect vacation. Call now or chat with a travel expert."
              />
            </p>
            <Link to={to(Routes.ConciergeTeam)} className="home-hilton__concierge-cta">
              <BlueButton>
                <FormattedMessage id="hilton.concierge.cta" defaultMessage="Talk to your Concierge" />
                <FontAwesomeIcon icon={faPhone} style={{ marginLeft: '8px' }} />
              </BlueButton>
            </Link>
          </div>
          <div className="home-hilton__concierge-img">
            <img src={ConciergeTeamImage} alt="Concierge team" />
          </div>
          <div className="home-hilton__concierge-award">
            <FontAwesomeIcon icon={faAward} className="home-hilton__concierge-award-icon" />
            <p className="home-hilton__concierge-award-title">
              <FormattedMessage id="hilton.concierge.award.title" defaultMessage="Award-Winning Service You Can Trust" />
            </p>
            <p className="home-hilton__concierge-award-copy">
              <FormattedMessage
                id="hilton.concierge.award.copy"
                defaultMessage="Recognized by leading travel partners and cruise lines."
              />
            </p>
          </div>
        </div>
      </section>

      {/* Find Inspiration */}
      <section className="home-hilton__inspiration">
        <div className="home-hilton__container">
          <div className="home-hilton__section-head">
            <div>
              <h2 className="home-hilton__section-title home-hilton__section-title--light">
                <FormattedMessage id="hilton.inspiration.title" defaultMessage="Find Inspiration" />
              </h2>
              <p className="home-hilton__inspiration-sub">
                <FormattedMessage
                  id="hilton.inspiration.sub"
                  defaultMessage="Explore our destination guides and travel ideas."
                />
              </p>
            </div>
            <Link to={to(Routes.Getaway)} className="home-hilton__section-link home-hilton__section-link--light">
              <FormattedMessage id="hilton.inspiration.viewAll" defaultMessage="Explore all getaways" />
              <FontAwesomeIcon icon={faArrowRight} />
            </Link>
          </div>
          <div className="home-hilton__deals-grid">
            {inspirationCards.map((card) => {
              const inner = (
                <div className="home-hilton__deal-body">
                  <span className="home-hilton__deal-body-title">
                    <h3><FormattedMessage id={card.title} /></h3>
                    <p><FormattedMessage id={card.description} /></p>
                  </span>
                </div>
              );

              return card.external ? (
                <a
                  href={card.href}
                  key={card.title}
                  className="home-hilton__deal-card"
                  style={{ backgroundImage: `url(${card.imageUrl})` }}
                  target="_blank"
                  rel="noreferrer"
                >
                  {inner}
                </a>
              ) : (
                <Link
                  to={card.href}
                  key={card.title}
                  className="home-hilton__deal-card"
                  style={{ backgroundImage: `url(${card.imageUrl})` }}
                >
                  {inner}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trusted by World-Class Partners */}
      <section className="home-hilton__container home-hilton__partners">
        <h2 className="home-hilton__section-title home-hilton__section-title--left">
          <FormattedMessage id="hilton.partners.title" defaultMessage="Trusted by World-Class Partners" />
        </h2>
        <Carousel {...partnersCarouselSettings} className="home-hilton__partners-carousel">
          {partners.map((p) => (
            <div className="home-hilton__partner-slide" key={p.name}>
              {p.image ? (
                <img src={p.image} alt={p.name} className="home-hilton__partner-logo-img" />
              ) : (
                <span className="home-hilton__partner-logo">{p.name}</span>
              )}
            </div>
          ))}
        </Carousel>
      </section>
    </div>
  );
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    loginStore: state.loginStore,
    menuStore: state.navigationMenuStore,
    benefitsStore: state.benefitsStore,
  };
};

export const HomeHilton = connect(mapStateToProps)(HomeHiltonComponent);
